import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createSessionController } from '@vista-cloud/web-common';
import { api, setVistaToken } from '../lib/api.js';
import { getOrgSlug } from '../lib/orgSlug.js';
import { buildAuthValue } from '../lib/authRole.js';

const AuthContext = createContext(null);

// #791: a NON-secret breadcrumb — org id + name only, NEVER the token — so that
// if a full-page reload drops the memory-only impersonation JWT (#673), the auth
// gate can explain "impersonation ended on refresh" + offer a resume link
// instead of the bare "use the Admin Console" message. This does NOT persist the
// token, so the #673 invariant holds.
const IMPERSONATION_HINT_KEY = 'vista_impersonation_hint';

export function getImpersonationHint() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_HINT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    loading: true,
    error: null,
    errorType: null, // 'not_provisioned' | 'auth_failed' | 'error'
  });
  // vista#671: the exchange → `/me` → refresh-timer plumbing and the 401/403
  // taxonomy live in `@vista-cloud/web-common`. They were hand-rolled here and
  // again in the admin console, and the two had drifted — the admin side still
  // answered a 401 with a timed `location.href` redirect that this console
  // removed in vista#372 because it loops forever when the subdomain is not
  // covered by a CF Access app policy.
  //
  // Memoised on stable callbacks, so it is created once: the controller owns a
  // timer, and re-creating it per render would leak one per render.

  const onUser = useCallback((user) => {
    setState({ user, loading: false, error: null, errorType: null });
  }, []);

  const onSessionError = useCallback(({ errorType, error }, rawError) => {
    // #679: don't swallow the cause. An expiring session that fails to renew
    // has to be diagnosable rather than looking like a silent logout.
    if (errorType === 'session_expired') {
      console.error('Session refresh failed:', rawError);
    }
    setState({ user: null, loading: false, error, errorType });
  }, []);

  const controller = useMemo(() => {
    const orgSlug = getOrgSlug();
    return createSessionController({
      api,
      setVistaToken,
      onUser,
      onError: onSessionError,
      // Subdomain routing: `stark.vista-cloud.io` proves which org the user is
      // asking for, and the worker validates they belong to it.
      sessionBody: orgSlug ? { org_slug: orgSlug } : undefined,
    });
  }, [onUser, onSessionError]);

  const clearRefreshTimer = useCallback(() => controller.cancelRefresh(), [controller]);

  const initSession = useCallback(() => controller.init(), [controller]);

  // Set impersonation token and fetch /me without re-initializing the session.
  // Used by the handoff flow to avoid overwriting the impersonation token.
  //
  // #673: the impersonation VISTA JWT is held in memory ONLY (via
  // setVistaToken, exactly like the normal session token) — never
  // persisted to sessionStorage/localStorage. This upholds the
  // "JWT in memory only" invariant (CLAUDE.md; lib/api.js). Trade-off:
  // a manual full-page refresh drops the impersonation session and falls
  // back to the normal vista_admin auth — accepted per #673.
  const startImpersonation = useCallback(async (impersonationToken) => {
    setVistaToken(impersonationToken);
    try {
      const me = await api.get('/me');
      const user = me.data;
      setState({ user, loading: false, error: null, errorType: null });
      // #791: drop the non-secret resume breadcrumb (org id/name only — NOT the
      // token) so a subsequent tokenless reload can offer a resume path.
      try {
        const imp = user?.impersonating;
        if (imp?.org_id) {
          sessionStorage.setItem(
            IMPERSONATION_HINT_KEY,
            JSON.stringify({ org_id: imp.org_id, org_name: imp.org_name }),
          );
        }
      } catch { /* sessionStorage unavailable — degrade to the generic gate */ }
      // No refresh timer — impersonation tokens have a 1-hour TTL and are not
      // refreshable, so a scheduled refresh would replace the impersonation
      // token with a normal session mid-impersonation.
      clearRefreshTimer();
    } catch (err) {
      setVistaToken(null);
      if (err.status === 401) {
        // Expired/invalid token — fall back to normal vista_admin session
        initSession();
      } else {
        setState({ user: null, loading: false, error: 'Impersonation failed', errorType: 'error' });
      }
    }
  }, [clearRefreshTimer, initSession]);

  const endImpersonation = useCallback(async () => {
    // #674: close the server-side impersonation_sessions record BEFORE
    // discarding the local token, so the audit trail records the session
    // end. This hits the ORG worker's /impersonate/end (not the admin
    // tier's /vista-admin/impersonate/end, which requires a real vista_admin
    // token and is unreachable with the org_admin impersonation token). The
    // call is made while the impersonation token is still set, so the worker
    // resolves the session from the token's own impersonation_id claim.
    try {
      await api.post('/impersonate/end');
    } catch (err) {
      // Don't strand the user if the close call fails (network blip, token
      // already expired server-side, etc.) — still tear down local state
      // below so they aren't stuck in the impersonation banner. Surface the
      // failure rather than swallowing it silently.
      console.error('Failed to close impersonation session on the server:', err);
    }
    // Clear impersonation token and re-init the normal session.
    setVistaToken(null);
    // #791: clear the resume breadcrumb — this was an intentional end, not a
    // dropped-on-reload session, so the gate shouldn't offer to resume.
    try { sessionStorage.removeItem(IMPERSONATION_HINT_KEY); } catch { /* ignore */ }
    await initSession();
  }, [initSession]);

  useEffect(() => {
    // Skip automatic session init on the /handoff route — the Handoff page
    // will call startImpersonation() directly with the handoff token.
    // Running initSession() here would race and wipe the impersonation token.
    if (window.location.pathname === '/handoff') return;

    // #673: no sessionStorage restore — the impersonation token lives in
    // memory only, so a full-page refresh intentionally drops it and we
    // start a normal session.
    initSession();
    return () => controller.dispose();
  }, [initSession, controller]);

  const imp = state.user?.impersonating || null;

  // Value object (incl. the isVistaAdmin gate UpdateButton reads) is
  // assembled by the pure, unit-tested buildAuthValue in lib/authRole.js
  // so the wiring itself is guarded against the field silently going
  // missing again (PR #566 review).
  const value = useMemo(
    () => buildAuthValue(state, imp, {
      refetch: initSession,
      startImpersonation,
      endImpersonation,
    }),
    [state, imp, initSession, startImpersonation, endImpersonation],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
